import React from 'react';
import { Typography, Button, IconButton, Snackbar, Alert } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Stack from '@mui/material/Stack';
import Box from '@mui/material/Box';

interface SubmissionSuccessProps {
  onReset?: () => void;
  fileName?: string;
}

export const SubmissionSuccess = ({ onReset, fileName }: SubmissionSuccessProps) => {
  const [showCopied, setShowCopied] = React.useState(false);

  const handleCopyFileName = () => {
    if (fileName) {
      navigator.clipboard.writeText(fileName);
      setShowCopied(true);
    }
  };

  const handleCloseCopied = () => {
    setShowCopied(false);
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Stack alignItems="center">
        <Typography align="center" component="h2" variant="h4" sx={{ mb: 3 }}>
          Success!
        </Typography>
        <CheckCircleIcon
          sx={{
            fontSize: '80px',
            color: 'success.light',
          }}
        />
      </Stack>

      {fileName && (
        <Box
          sx={{
            mt: 3,
            p: 2,
            backgroundColor: (theme) =>
              theme.palette.mode === 'dark' ? 'grey.800' : 'grey.100',
            borderRadius: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <Box sx={{ flex: 1, mr: 1 }}>
            <Typography variant="caption" color="text.secondary" display="block">
              Generated File Name:
            </Typography>
            <Typography
              variant="body2"
              sx={{
                wordBreak: 'break-all',
                fontFamily: 'monospace',
              }}
            >
              {fileName}
            </Typography>
          </Box>
          <IconButton
            onClick={handleCopyFileName}
            color="primary"
            size="small"
            title="Copy filename"
          >
            <ContentCopyIcon fontSize="small" />
          </IconButton>
        </Box>
      )}

      <Typography sx={{ mt: 4 }}>
        You will receive an email shortly with links to check status or
        terminate your request. This email may take a few minutes to arrive.
      </Typography>
      {onReset && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={onReset}
            size="large"
          >
            Submit Another Request
          </Button>
        </Box>
      )}

      <Snackbar
        open={showCopied}
        autoHideDuration={2000}
        onClose={handleCloseCopied}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseCopied} severity="success" sx={{ width: '100%' }}>
          Filename copied to clipboard!
        </Alert>
      </Snackbar>
    </Box>
  );
};
